import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { heroSlides } from '@/mocks/site';

export default function HeroCarousel() {
  const [current, setCurrent] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const go = useCallback((next: number) => {
    setCurrent((next + heroSlides.length) % heroSlides.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % heroSlides.length);
    }, 5500);
    return () => clearInterval(timer);
  }, []);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      delta < 0 ? go(current + 1) : go(current - 1);
    }
    touchStartX.current = null;
  };

  const slide = heroSlides[current];

  return (
    <section className="relative w-full h-[520px] md:h-[660px] overflow-hidden bg-primary-950">
      {heroSlides.map((item, index) => (
        <div
          key={item.id}
          className={[
            'absolute inset-0 transition-opacity duration-700 ease-out',
            index === current ? 'opacity-100' : 'opacity-0',
          ].join(' ')}
        >
          <img
            src={item.image}
            alt={`${item.title} 焦点剧照`}
            title={`${item.title} 最新资讯`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-primary-950/40" />

      <div className="relative z-10 h-full w-full max-w-[1280px] mx-auto px-4 md:px-8 flex items-center">
        <div key={slide.id} className="max-w-xl animate-fade-up">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-500/90 text-primary-950 text-xs font-semibold mb-4">
            <i className="ri-fire-line" />
            {slide.kicker}
          </span>
          <h1 className="font-heading font-black text-3xl md:text-5xl text-background-50 leading-tight mb-4">
            {slide.title}
          </h1>
          <p className="text-sm md:text-lg text-background-100/85 leading-relaxed mb-7 max-w-lg">
            {slide.description}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${slide.filmId}`}
              className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-circle-line text-lg" />
              查看剧情详情
            </Link>
            <Link
              to="/category"
              className="flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-50/40 text-background-50 text-sm font-medium hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-compass-3-line text-lg" />
              浏览全部影视
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-0 right-0 z-10 max-w-[1280px] mx-auto px-4 md:px-8 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {heroSlides.map((item, index) => (
            <button
              key={item.id}
              type="button"
              aria-label={`切换到第 ${index + 1} 张`}
              onClick={() => setCurrent(index)}
              className={[
                'h-1.5 rounded-full transition-all duration-300 cursor-pointer',
                index === current ? 'w-8 bg-accent-500' : 'w-3 bg-background-50/50 hover:bg-background-50/80',
              ].join(' ')}
            />
          ))}
        </div>
        <div className="hidden md:flex items-center gap-2">
          <button
            type="button"
            aria-label="上一张"
            onClick={() => go(current - 1)}
            className="flex items-center justify-center w-10 h-10 rounded-full border border-background-50/40 text-background-50 hover:bg-background-50/15 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
          <button
            type="button"
            aria-label="下一张"
            onClick={() => go(current + 1)}
            className="flex items-center justify-center w-10 h-10 rounded-full border border-background-50/40 text-background-50 hover:bg-background-50/15 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-xl" />
          </button>
        </div>
      </div>
    </section>
  );
}